import React, { useState, useEffect, useRef } from 'react';
import { 
  Card, Typography, Steps, Button, Space, Select, Input, Table, Form, 
  Tabs, Alert, Row, Col, Drawer, Modal, Checkbox, Radio, Divider,
  Tag, Timeline, message, Tooltip, Badge, Collapse, List, Progress
} from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  EditOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ArrowRightOutlined,
  DatabaseOutlined,
  ApartmentOutlined,
  LinkOutlined,
  HighlightOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import * as d3 from 'd3';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;
const { Panel } = Collapse;
const { Option } = Select;
const { TextArea } = Input;

// 类型定义
interface Entity {
  id: string;
  name: string;
  type: 'aggregate' | 'entity' | 'valueObject';
  attributes: Attribute[];
  description?: string;
  x?: number;
  y?: number;
}

interface Attribute {
  id: string;
  name: string;
  type: string;
  required: boolean;
  description?: string;
}

interface Relationship {
  id: string;
  source: string;
  target: string;
  type: '1:1' | '1:N' | 'N:N';
  name: string;
}

interface BusinessProcess {
  id: string;
  name: string;
  description: string;
  steps: ProcessStep[];
}

interface ProcessStep {
  id: string;
  name: string;
  entities: string[];
  actions: string[];
}

const InteractiveDesignTutorial: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playProgress, setPlayProgress] = useState(0);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [selectedProcess, setSelectedProcess] = useState<string>('order');
  const [editMode, setEditMode] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // 业务流程数据
  const businessProcesses: BusinessProcess[] = [
    {
      id: 'order',
      name: '订单创建流程',
      description: '从客户下单到订单确认的完整流程',
      steps: [
        {
          id: 'step1',
          name: '客户选择与验证',
          entities: ['Customer', 'CustomerCredit'],
          actions: ['选择客户', '验证信用额度', '检查黑名单']
        },
        {
          id: 'step2',
          name: '货物信息录入',
          entities: ['Cargo', 'HSCode', 'DangerousGoods'],
          actions: ['录入货物信息', '选择HS编码', '标记危险品属性']
        },
        {
          id: 'step3',
          name: '路线与服务选择',
          entities: ['Route', 'Service', 'Carrier'],
          actions: ['选择运输路线', '选择服务类型', '分配承运商']
        },
        {
          id: 'step4',
          name: '费用计算与报价',
          entities: ['Quotation', 'ChargeItem', 'Currency'],
          actions: ['计算运费', '添加附加费', '生成报价单']
        },
        {
          id: 'step5',
          name: '订单生成',
          entities: ['Order', 'OrderItem', 'OrderStatus'],
          actions: ['创建订单', '关联客户和货物', '设置初始状态']
        }
      ]
    },
    {
      id: 'customer',
      name: '客户主数据管理流程',
      description: '客户信息的创建、审核和维护流程',
      steps: [
        {
          id: 'step1',
          name: '总部信息创建',
          entities: ['CustomerHeadquarters', 'UnifiedCreditCode'],
          actions: ['录入统一信用码', '填写公司名称', '设置国家地区']
        },
        {
          id: 'step2',
          name: '部门客商分配',
          entities: ['DepartmentCustomer', 'Department', 'BusinessLine'],
          actions: ['分配到部门', '设置客户类型', '关联业务线']
        },
        {
          id: 'step3',
          name: '扩展信息配置',
          entities: ['CustomerExtension', 'Contact', 'BankAccount'],
          actions: ['添加联系人', '配置银行账号', '设置结算信息']
        },
        {
          id: 'step4',
          name: '信用与风控',
          entities: ['CreditLimit', 'RiskLevel', 'Blacklist'],
          actions: ['设置信用额度', '评估风险等级', '管理黑名单']
        }
      ]
    },
    {
      id: 'shipment-tracking',
      name: '运输跟踪流程',
      description: '从下单到交付的全程跟踪管理',
      steps: [
        {
          id: 'step1',
          name: '运输单创建',
          entities: ['Shipment', 'ShipmentNo', 'Route'],
          actions: ['生成运输单号', '关联订单', '确认路线']
        },
        {
          id: 'step2',
          name: '装货操作',
          entities: ['Container', 'LoadingPlan', 'PackingList'],
          actions: ['分配集装箱', '制定装箱计划', '生成装箱单']
        },
        {
          id: 'step3',
          name: '运输监控',
          entities: ['TrackingEvent', 'Location', 'Status'],
          actions: ['更新位置', '记录事件', '推送通知']
        },
        {
          id: 'step4',
          name: '交付确认',
          entities: ['DeliveryOrder', 'ProofOfDelivery', 'Invoice'],
          actions: ['生成交付单', '确认签收', '开具发票']
        }
      ]
    }
  ];

  // 预定义的实体模板
  const entityTemplates = {
    Customer: {
      type: 'entity' as const,
      displayName: '客户',
      attributes: [
        { id: 'cust1', name: 'customerId', type: 'string', required: true, description: '客户唯一标识' },
        { id: 'cust2', name: 'name', type: 'string', required: true, description: '客户名称' },
        { id: 'cust3', name: 'type', type: 'enum', required: true, description: '客户类型：直客/同行' },
        { id: 'cust4', name: 'status', type: 'enum', required: true, description: '客户状态' }
      ]
    },
    Order: {
      type: 'aggregate' as const,
      displayName: '订单',
      attributes: [
        { id: 'ord1', name: 'orderId', type: 'string', required: true, description: '订单号' },
        { id: 'ord2', name: 'customerId', type: 'string', required: true, description: '客户ID' },
        { id: 'ord3', name: 'orderDate', type: 'date', required: true, description: '下单日期' },
        { id: 'ord4', name: 'totalAmount', type: 'Money', required: true, description: '总金额' }
      ]
    },
    Route: {
      type: 'valueObject' as const,
      displayName: '路线',
      attributes: [
        { id: 'rt1', name: 'origin', type: 'Port', required: true, description: '起运港' },
        { id: 'rt2', name: 'destination', type: 'Port', required: true, description: '目的港' },
        { id: 'rt3', name: 'transitPorts', type: 'Port[]', required: false, description: '中转港' }
      ]
    },
    Shipment: {
      type: 'entity' as const,
      displayName: '运输单',
      attributes: [
        { id: 'ship1', name: 'shipmentNo', type: 'string', required: true, description: '运输单号' },
        { id: 'ship2', name: 'orderId', type: 'string', required: true, description: '订单ID' },
        { id: 'ship3', name: 'status', type: 'enum', required: true, description: '运输状态' },
        { id: 'ship4', name: 'eta', type: 'date', required: false, description: '预计到达时间' }
      ]
    },
    Container: {
      type: 'entity' as const,
      displayName: '集装箱',
      attributes: [
        { id: 'cont1', name: 'containerNo', type: 'string', required: true, description: '箱号' },
        { id: 'cont2', name: 'type', type: 'enum', required: true, description: '箱型：20GP/40GP/40HQ' },
        { id: 'cont3', name: 'sealNo', type: 'string', required: false, description: '封号' }
      ]
    },
    TrackingEvent: {
      type: 'entity' as const,
      displayName: '跟踪事件',
      attributes: [
        { id: 'evt1', name: 'eventId', type: 'string', required: true, description: '事件ID' },
        { id: 'evt2', name: 'eventType', type: 'enum', required: true, description: '事件类型' },
        { id: 'evt3', name: 'timestamp', type: 'datetime', required: true, description: '发生时间' },
        { id: 'evt4', name: 'location', type: 'Location', required: true, description: '发生地点' }
      ]
    },
    DeliveryOrder: {
      type: 'entity' as const,
      displayName: '交付单',
      attributes: [
        { id: 'del1', name: 'deliveryNo', type: 'string', required: true, description: '交付单号' },
        { id: 'del2', name: 'shipmentNo', type: 'string', required: true, description: '运输单号' },
        { id: 'del3', name: 'consignee', type: 'string', required: true, description: '收货人' },
        { id: 'del4', name: 'deliveryDate', type: 'date', required: false, description: '交付日期' }
      ]
    }
  };

  // 教学步骤
  const tutorialSteps = [
    {
      title: '理解业务流程',
      description: '首先选择一个业务流程，理解其中的业务活动和参与者'
    },
    {
      title: '识别核心实体',
      description: '从业务流程中找出关键的业务概念，这些将成为我们的实体'
    },
    {
      title: '定义实体属性',
      description: '为每个实体添加必要的属性，思考业务需要记录哪些信息'
    },
    {
      title: '建立初步关系',
      description: '分析实体之间的业务关系，确定关系类型（1:1, 1:N, N:N）'
    },
    {
      title: '费用计算与报价',
      description: '添加报价单、费用项、路线和运输单，完成订单创建全流程'
    }
  ];

  // 绘制实体关系图
  const drawDiagram = () => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 800;
    const height = 500;
    
    svg.attr('width', width).attr('height', height);

    // 如果是编辑模式，添加网格背景
    if (editMode) {
      const defs = svg.append('defs');
      const pattern = defs.append('pattern')
        .attr('id', 'grid')
        .attr('width', 20)
        .attr('height', 20)
        .attr('patternUnits', 'userSpaceOnUse');
      
      pattern.append('path')
        .attr('d', 'M 20 0 L 0 0 0 20')
        .attr('fill', 'none')
        .attr('stroke', '#e0e0e0')
        .attr('stroke-width', 0.5);
      
      svg.append('rect')
        .attr('width', width)
        .attr('height', height)
        .attr('fill', 'url(#grid)');
    }

    // 创建箭头标记
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 10)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#999');

    // 绘制关系线
    const linkGroup = svg.append('g').attr('class', 'links');
    relationships.forEach(rel => {
      const source = entities.find(e => e.id === rel.source);
      const target = entities.find(e => e.id === rel.target);
      if (source && target) {
        const line = linkGroup.append('line')
          .attr('x1', source.x || 100)
          .attr('y1', source.y || 100)
          .attr('x2', target.x || 200)
          .attr('y2', target.y || 200)
          .attr('stroke', '#999')
          .attr('stroke-width', 2)
          .attr('marker-end', 'url(#arrowhead)');
        
        // 添加关系标签
        const midX = ((source.x || 100) + (target.x || 200)) / 2;
        const midY = ((source.y || 100) + (target.y || 200)) / 2;
        
        // 关系中文名映射
        const relNameCN = rel.name === 'has' ? '拥有' :
                         rel.name === 'contains' ? '包含' :
                         rel.name === 'places' ? '下单' :
                         rel.name === 'belongs to' ? '属于' :
                         rel.name === 'uses' ? '使用' :
                         rel.name === 'generates' ? '生成' :
                         rel.name;
        
        // 关系类型中文映射
        const relTypeCN = rel.type === '1:1' ? '一对一' :
                         rel.type === '1:N' ? '一对多' :
                         rel.type === 'N:N' ? '多对多' :
                         rel.type;
        
        linkGroup.append('text')
          .attr('x', midX)
          .attr('y', midY - 10)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('fill', '#666')
          .text(relNameCN);
          
        linkGroup.append('text')
          .attr('x', midX)
          .attr('y', midY + 5)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#999')
          .text(`(${rel.type})`);
      }
    });

    // 绘制实体
    const nodeGroup = svg.append('g').attr('class', 'nodes');
    
    const drag = d3.drag<any, Entity>()
      .on('start', function(event, d) {
        d3.select(this).raise().classed('active', true);
      })
      .on('drag', function(event, d) {
        d.x = event.x;
        d.y = event.y;
        d3.select(this).attr('transform', `translate(${d.x}, ${d.y})`);
        
        // 更新连线
        svg.selectAll('line')
          .attr('x1', (lineData: any) => {
            const rel = relationships.find(r => r.id === lineData.id);
            const source = entities.find(e => e.id === rel?.source);
            return source?.x || 100;
          })
          .attr('y1', (lineData: any) => {
            const rel = relationships.find(r => r.id === lineData.id);
            const source = entities.find(e => e.id === rel?.source);
            return source?.y || 100;
          })
          .attr('x2', (lineData: any) => {
            const rel = relationships.find(r => r.id === lineData.id);
            const target = entities.find(e => e.id === rel?.target);
            return target?.x || 200;
          })
          .attr('y2', (lineData: any) => {
            const rel = relationships.find(r => r.id === lineData.id);
            const target = entities.find(e => e.id === rel?.target);
            return target?.y || 200;
          });
      })
      .on('end', function(event, d) {
        d3.select(this).classed('active', false);
      });

    entities.forEach(entity => {
      const g = nodeGroup.append('g')
        .datum(entity)
        .attr('transform', `translate(${entity.x || 100}, ${entity.y || 100})`)
        .attr('cursor', editMode ? 'move' : 'pointer')
        .on('click', () => {
          if (editMode) {
            setSelectedEntity(entity);
            setDrawerVisible(true);
          }
        });
      
      if (editMode) {
        g.call(drag);
      }

      // 根据类型选择颜色
      const color = entity.type === 'aggregate' ? '#1890ff' : 
                   entity.type === 'entity' ? '#52c41a' : '#faad14';
      
      // 实体背景
      const rect = g.append('rect')
        .attr('x', -90)
        .attr('y', -35)
        .attr('width', 180)
        .attr('height', 85 + entity.attributes.length * 16)
        .attr('rx', 5)
        .attr('fill', 'white')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .style('opacity', 0)
        .transition()
        .duration(500)
        .style('opacity', 1);
      
      // 实体名称（英文）
      g.append('text')
        .attr('x', 0)
        .attr('y', -15)
        .attr('text-anchor', 'middle')
        .attr('font-size', '16px')
        .attr('font-weight', 'bold')
        .attr('fill', color)
        .text(entity.name);
      
      // 实体中文名
      const chineseName = entity.name === 'Customer' ? '客户' :
                          entity.name === 'Order' ? '订单' :
                          entity.name === 'Cargo' ? '货物' :
                          entity.name === 'Route' ? '路线' :
                          entity.name === 'Shipment' ? '运输单' :
                          entity.name === 'Service' ? '服务' :
                          entity.name === 'Quotation' ? '报价' :
                          entity.name;
      
      g.append('text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', color)
        .text(chineseName);
      
      // 实体类型标签
      const typeLabel = entity.type === 'aggregate' ? '聚合根' :
                       entity.type === 'entity' ? '实体' : '值对象';
      g.append('text')
        .attr('x', 0)
        .attr('y', 15)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', '#999')
        .text(`<<${typeLabel}>>`);
      
      // 属性列表
      entity.attributes.forEach((attr, i) => {
        // 如果有业务描述，优先显示描述，否则显示类型
        const displayText = attr.description || (() => {
          const chineseType = attr.type === 'string' ? '字符串' :
                             attr.type === 'number' ? '数字' :
                             attr.type === 'date' ? '日期' :
                             attr.type === 'boolean' ? '布尔' :
                             attr.type === 'enum' ? '枚举' :
                             attr.type;
          return chineseType;
        })();
        
        g.append('text')
          .attr('x', -80)
          .attr('y', 40 + i * 15)
          .attr('font-size', '11px')
          .attr('fill', '#333')
          .text(`${attr.name}: ${displayText}`);
          
        // 如果有描述，添加提示
        if (attr.description) {
          g.append('title')
            .text(attr.description);
        }
      });

      // 编辑模式下添加操作按钮
      if (editMode) {
        g.append('circle')
          .attr('cx', 70)
          .attr('cy', -20)
          .attr('r', 10)
          .attr('fill', '#ff4d4f')
          .attr('cursor', 'pointer')
          .on('click', (event) => {
            event.stopPropagation();
            removeEntity(entity.id);
          });
        
        g.append('text')
          .attr('x', 70)
          .attr('y', -15)
          .attr('text-anchor', 'middle')
          .attr('font-size', '16px')
          .attr('fill', 'white')
          .attr('pointer-events', 'none')
          .text('×');
      }
    });
  };

  // 添加实体
  const addEntity = (name: string, template?: keyof typeof entityTemplates) => {
    const newEntity: Entity = {
      id: `entity_${Date.now()}`,
      name,
      type: template ? entityTemplates[template].type : 'entity',
      attributes: template ? [...entityTemplates[template].attributes] : [],
      description: template ? entityTemplates[template].displayName : undefined,
      x: 100 + entities.length * 150,
      y: 100 + (entities.length % 3) * 100
    };
    setEntities([...entities, newEntity]);
  };

  // 删除实体
  const removeEntity = (id: string) => {
    setEntities(entities.filter(e => e.id !== id));
    // 同时删除相关的关系
    setRelationships(relationships.filter(r => r.source !== id && r.target !== id));
  };

  // 添加关系
  const addRelationship = (source: string, target: string, type: '1:1' | '1:N' | 'N:N', name: string) => {
    const newRel: Relationship = {
      id: `rel_${Date.now()}`,
      source,
      target,
      type,
      name
    };
    setRelationships([...relationships, newRel]);
  };

  // 更新实体
  const updateEntity = (entity: Entity) => {
    setEntities(entities.map(e => e.id === entity.id ? entity : e));
  };

  // 从流程步骤中提取实体
  const extractEntitiesFromStep = (step: ProcessStep) => {
    step.entities.forEach(entityName => {
      if (!entities.find(e => e.name === entityName)) {
        const template = entityTemplates[entityName as keyof typeof entityTemplates];
        if (template) {
          addEntity(entityName, entityName as keyof typeof entityTemplates);
        } else {
          addEntity(entityName);
        }
      }
    });
  };

  // 根据步骤自动展示内容
  const demonstrateStep = (step: number) => {
    switch (step) {
      case 0: // 理解业务流程
        // 清空所有实体，准备演示
        setEntities([]);
        setRelationships([]);
        break;
        
      case 1: // 识别实体
        // 添加核心实体
        const customer: Entity = {
          id: 'demo_customer',
          name: 'Customer',
          type: 'entity',
          attributes: [],
          x: 200,
          y: 150
        };
        const order: Entity = {
          id: 'demo_order',
          name: 'Order',
          type: 'aggregate',
          attributes: [],
          x: 500,
          y: 150
        };
        setEntities([customer, order]);
        break;
        
      case 2: // 定义属性
        // 为实体添加属性
        setEntities(prev => prev.map(entity => {
          if (entity.id === 'demo_customer') {
            return {
              ...entity,
              attributes: [
                { id: 'attr1', name: 'customerId', type: 'string', required: true, description: '客户编号' },
                { id: 'attr2', name: 'name', type: 'string', required: true, description: '客户名称' },
                { id: 'attr3', name: 'creditLimit', type: 'number', required: false, description: '信用额度' }
              ]
            };
          } else if (entity.id === 'demo_order') {
            return {
              ...entity,
              attributes: [
                { id: 'attr4', name: 'orderId', type: 'string', required: true, description: '订单编号' },
                { id: 'attr5', name: 'orderDate', type: 'date', required: true, description: '下单日期' },
                { id: 'attr6', name: 'totalAmount', type: 'Money', required: true, description: '总金额' }
              ]
            };
          }
          return entity;
        }));
        break;
        
      case 3: // 建立初步关系
        // 添加实体关系
        const rel: Relationship = {
          id: 'demo_rel1',
          source: 'demo_customer',
          target: 'demo_order',
          type: '1:N',
          name: 'places'
        };
        setRelationships([rel]);
        
        // 添加更多实体
        const cargo: Entity = {
          id: 'demo_cargo',
          name: 'Cargo',
          type: 'entity',
          attributes: [
            { id: 'attr7', name: 'description', type: 'string', required: true, description: '货物描述' },
            { id: 'attr8', name: 'weight', type: 'number', required: true, description: '重量' }
          ],
          x: 350,
          y: 300
        };
        setEntities(prev => [...prev, cargo]);
        
        // 添加订单到货物的关系
        setTimeout(() => {
          const rel2: Relationship = {
            id: 'demo_rel2',
            source: 'demo_order',
            target: 'demo_cargo',
            type: '1:N',
            name: 'contains'
          };
          setRelationships(prev => [...prev, rel2]);
        }, 2000);
        break;
        
      case 4: // 费用计算与报价
        // 添加费用计算相关的实体
        const quotation: Entity = {
          id: 'demo_quotation',
          name: 'Quotation',
          type: 'entity',
          attributes: [
            { id: 'attr9', name: 'quotationNo', type: 'string', required: true, description: '报价单号' },
            { id: 'attr10', name: 'validUntil', type: 'date', required: true, description: '有效期至' },
            { id: 'attr11', name: 'totalAmount', type: 'Money', required: true, description: '总金额' }
          ],
          x: 650,
          y: 150
        };
        
        const chargeItem: Entity = {
          id: 'demo_chargeItem',
          name: 'ChargeItem',
          type: 'valueObject',
          attributes: [
            { id: 'attr12', name: 'chargeCode', type: 'string', required: true, description: '费用代码' },
            { id: 'attr13', name: 'chargeName', type: 'string', required: true, description: '费用名称' },
            { id: 'attr14', name: 'amount', type: 'Money', required: true, description: '金额' },
            { id: 'attr15', name: 'currency', type: 'Currency', required: true, description: '币种' }
          ],
          x: 650,
          y: 350
        };
        
        const route: Entity = {
          id: 'demo_route',
          name: 'Route',
          type: 'valueObject',
          attributes: [
            { id: 'attr16', name: 'origin', type: 'Port', required: true, description: '起运港' },
            { id: 'attr17', name: 'destination', type: 'Port', required: true, description: '目的港' }
          ],
          x: 200,
          y: 450
        };
        
        setEntities(prev => [...prev, quotation, chargeItem, route]);
        
        // 添加费用相关的关系
        setTimeout(() => {
          const newRels: Relationship[] = [
            {
              id: 'demo_rel3',
              source: 'demo_order',
              target: 'demo_quotation',
              type: '1:1',
              name: 'has'
            },
            {
              id: 'demo_rel4',
              source: 'demo_quotation',
              target: 'demo_chargeItem',
              type: '1:N',
              name: 'includes'
            },
            {
              id: 'demo_rel5',
              source: 'demo_order',
              target: 'demo_route',
              type: '1:1',
              name: 'uses'
            }
          ];
          setRelationships(prev => [...prev, ...newRels]);
          
          // 继续添加运输单实体
          setTimeout(() => {
            const shipment: Entity = {
              id: 'demo_shipment',
              name: 'Shipment',
              type: 'entity',
              attributes: [
                { id: 'attr18', name: 'shipmentNo', type: 'string', required: true, description: '运输单号' },
                { id: 'attr19', name: 'status', type: 'enum', required: true, description: '运输状态' }
              ],
              x: 450,
              y: 450
            };
            
            setEntities(prev => [...prev, shipment]);
            
            const shipmentRel: Relationship = {
              id: 'demo_rel6',
              source: 'demo_order',
              target: 'demo_shipment',
              type: '1:1',
              name: 'generates'
            };
            setRelationships(prev => [...prev, shipmentRel]);
            
            // 显示完成消息
            setTimeout(() => {
              message.success('订单创建流程的完整实体模型设计完成！');
              
              // 显示统计信息
              const entityCount = entities.length + 4; // 加上刚添加的四个
              const relCount = relationships.length + 4;
              message.info(`共创建了 ${entityCount} 个实体，${relCount} 个关系`);
            }, 1000);
          }, 1500);
        }, 1500);
        break;
    }
  };

  // 自动播放教程
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let progressTimer: NodeJS.Timeout;
    
    if (isPlaying) {
      // 重置进度
      setPlayProgress(0);
      
      // 更新进度条
      progressTimer = setInterval(() => {
        setPlayProgress(prev => Math.min(prev + 2, 100));
      }, 100);
      
      if (currentStep < tutorialSteps.length - 1) {
        // 5秒后进入下一步
        timer = setTimeout(() => {
          setCurrentStep(currentStep + 1);
          message.info(`进入步骤 ${currentStep + 2}: ${tutorialSteps[currentStep + 1].title}`);
        }, 5000);
      } else {
        // 在最后一步，等待5秒后完成
        timer = setTimeout(() => {
          setIsPlaying(false);
          message.success('教程播放完成！');
        }, 5000);
      }
    }
    
    return () => {
      if (timer) clearTimeout(timer);
      if (progressTimer) clearInterval(progressTimer);
    };
  }, [isPlaying, currentStep]);

  // 当前步骤变化时执行演示
  useEffect(() => {
    if (isPlaying || currentStep === 0) {
      demonstrateStep(currentStep);
    }
  }, [currentStep]);

  // 绘制图表
  useEffect(() => {
    drawDiagram();
  }, [entities, relationships, editMode]);

  return (
    <div>
      <Title level={2}>交互式实体设计教程</Title>
      <Paragraph>
        通过实际业务流程，一步步学习如何识别实体、定义属性、建立关系，并支持自定义绘图。
      </Paragraph>

      <Alert
        message="学习提示"
        description={
          <div>
            <p>🎯 <strong>目标</strong>：从业务流程中提取领域模型</p>
            <p>📚 <strong>方法</strong>：跟随教程步骤，或自由探索编辑模式</p>
            <p>✏️ <strong>实践</strong>：在编辑模式下可以拖拽实体、添加属性、建立关系</p>
          </div>
        }
        type="info"
        style={{ marginBottom: 16 }}
      />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title="教程进度" 
            extra={
              <Space>
                <Button
                  icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  onClick={() => {
                    if (!isPlaying) {
                      // 开始播放
                      setIsPlaying(true);
                      if (currentStep === tutorialSteps.length - 1) {
                        // 如果已经在最后一步，从头开始
                        setCurrentStep(0);
                        setEntities([]);
                        setRelationships([]);
                      }
                    } else {
                      // 暂停播放
                      setIsPlaying(false);
                    }
                  }}
                  disabled={isPlaying && currentStep === tutorialSteps.length - 1}
                >
                  {isPlaying ? '暂停' : '自动播放'}
                </Button>
                <Button
                  icon={<EditOutlined />}
                  type={editMode ? 'primary' : 'default'}
                  onClick={() => setEditMode(!editMode)}
                >
                  {editMode ? '退出编辑' : '编辑模式'}
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setEntities([]);
                    setRelationships([]);
                    setCurrentStep(0);
                    setIsPlaying(false);
                    setPlayProgress(0);
                    message.info('已重置演示');
                  }}
                >
                  重置
                </Button>
              </Space>
            }
          >
            <Steps current={currentStep} onChange={(step) => {
              setCurrentStep(step);
              if (isPlaying) {
                demonstrateStep(step);
              }
            }}>
              {tutorialSteps.map((step, index) => (
                <Step 
                  key={index} 
                  title={step.title} 
                  description={step.description}
                  icon={currentStep === index && isPlaying ? <LoadingOutlined /> : undefined}
                />
              ))}
            </Steps>
            {isPlaying && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">自动播放进度：</Text>
                <Progress 
                  percent={playProgress} 
                  size="small" 
                  status="active"
                  showInfo={false}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={8}>
          <Card title="业务流程选择">
            <Form layout="vertical">
              <Form.Item label="选择业务流程">
                <Select 
                  value={selectedProcess} 
                  onChange={setSelectedProcess}
                  style={{ width: '100%' }}
                >
                  {businessProcesses.map(process => (
                    <Option key={process.id} value={process.id}>
                      {process.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <Title level={5}>
                {businessProcesses.find(p => p.id === selectedProcess)?.name}
              </Title>
              <Paragraph type="secondary">
                {businessProcesses.find(p => p.id === selectedProcess)?.description}
              </Paragraph>
            </div>

            <Collapse defaultActiveKey={['step0']}>
              {businessProcesses.find(p => p.id === selectedProcess)?.steps.map((step, index) => (
                <Panel 
                  header={`步骤 ${index + 1}: ${step.name}`} 
                  key={`step${index}`}
                  extra={
                    <Button 
                      size="small" 
                      type="link"
                      onClick={(e) => {
                        e.stopPropagation();
                        extractEntitiesFromStep(step);
                        message.success('已提取实体');
                      }}
                    >
                      提取实体
                    </Button>
                  }
                >
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>涉及实体：</Text>
                    <div>
                      {step.entities.map(entity => (
                        <Tag key={entity} color="blue" style={{ marginTop: 4 }}>
                          {entity}
                        </Tag>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Text strong>业务动作：</Text>
                    <List
                      size="small"
                      dataSource={step.actions}
                      renderItem={action => (
                        <List.Item>
                          <ArrowRightOutlined /> {action}
                        </List.Item>
                      )}
                    />
                  </div>
                </Panel>
              ))}
            </Collapse>

            {editMode && (
              <Card title="快速添加" style={{ marginTop: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button 
                    icon={<PlusOutlined />} 
                    block
                    onClick={() => {
                      const name = prompt('请输入实体名称');
                      if (name) addEntity(name);
                    }}
                  >
                    添加自定义实体
                  </Button>
                  <Select
                    placeholder="从模板添加实体"
                    style={{ width: '100%' }}
                    onChange={(value) => addEntity(value, value as keyof typeof entityTemplates)}
                  >
                    {Object.entries(entityTemplates).map(([key, template]) => (
                      <Option key={key} value={key}>
                        {key} - {template.displayName}
                      </Option>
                    ))}
                  </Select>
                </Space>
              </Card>
            )}
          </Card>
        </Col>

        <Col span={16}>
          <Card 
            title="实体关系图" 
            extra={
              currentStep < tutorialSteps.length && (
                <Tag color="blue">{tutorialSteps[currentStep].title}</Tag>
              )
            }
          >
            <div style={{ position: 'relative' }}>
              <svg ref={svgRef} style={{ border: '1px solid #f0f0f0', width: '100%' }}></svg>
              
              {editMode && entities.length > 1 && (
                <div style={{ position: 'absolute', top: 10, right: 10 }}>
                  <Button
                    icon={<LinkOutlined />}
                    size="small"
                    onClick={() => {
                      if (entities.length >= 2) {
                        const source = entities[entities.length - 2];
                        const target = entities[entities.length - 1];
                        addRelationship(source.id, target.id, '1:N', 'has');
                        message.success('已添加关系');
                      }
                    }}
                  >
                    快速连接最后两个实体
                  </Button>
                </div>
              )}
            </div>

            <Divider />

            {/* 当前步骤的详细说明 */}
            <Card type="inner" title={`步骤 ${currentStep + 1}: ${tutorialSteps[currentStep]?.title}`}>
              {currentStep === 0 && (
                <div>
                  <Paragraph>
                    业务流程是实体设计的起点。观察左侧的业务流程，思考：
                  </Paragraph>
                  <ul>
                    <li>这个流程涉及哪些参与者？（可能成为实体）</li>
                    <li>流程中操作的是什么对象？（核心业务概念）</li>
                    <li>需要记录哪些信息？（实体的属性）</li>
                  </ul>
                  <Alert
                    message="试试看"
                    description="点击左侧流程步骤中的'提取实体'按钮，自动识别该步骤中的实体。"
                    type="success"
                  />
                </div>
              )}

              {currentStep === 1 && (
                <div>
                  <Paragraph>
                    实体是业务中的核心概念，通常是名词。在DDD中，实体分为：
                  </Paragraph>
                  <Space direction="vertical">
                    <Tag color="blue">聚合根（Aggregate）- 独立存在的核心实体</Tag>
                    <Tag color="green">实体（Entity）- 有唯一标识的业务对象</Tag>
                    <Tag color="orange">值对象（Value Object）- 描述性的、不可变的对象</Tag>
                  </Space>
                  <Alert
                    message="演示内容"
                    description="将创建Customer（客户）和Order（订单）两个核心实体"
                    type="info"
                  />
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  <Paragraph>
                    属性定义了实体需要记录的信息。思考每个属性的：
                  </Paragraph>
                  <ul>
                    <li><strong>名称</strong>：业务上如何称呼这个信息</li>
                    <li><strong>类型</strong>：string、number、date、enum等</li>
                    <li><strong>必填性</strong>：是否业务必需</li>
                    <li><strong>业务含义</strong>：为什么需要这个属性</li>
                  </ul>
                  <Alert
                    message="编辑提示"
                    description="进入编辑模式后，点击实体可以编辑其属性。"
                    type="info"
                  />
                </div>
              )}

              {currentStep === 3 && (
                <div>
                  <Paragraph>
                    建立核心实体之间的关系：
                  </Paragraph>
                  <List
                    size="small"
                    dataSource={[
                      { type: '1:1', desc: '一对一：如订单与运输单' },
                      { type: '1:N', desc: '一对多：如客户与订单' },
                      { type: 'N:N', desc: '多对多：如货物与服务' }
                    ]}
                    renderItem={item => (
                      <List.Item>
                        <Tag color="purple">{item.type}</Tag> {item.desc}
                      </List.Item>
                    )}
                  />
                  <Alert
                    message="演示内容"
                    description="将展示Customer下单Order，Order包含Cargo的关系"
                    type="info"
                  />
                </div>
              )}

              {currentStep === 4 && (
                <div>
                  <Paragraph>
                    完善订单创建流程的领域模型：
                  </Paragraph>
                  <Timeline>
                    <Timeline.Item color="blue">
                      添加Route（路线）值对象 - 管理起运港和目的港
                    </Timeline.Item>
                    <Timeline.Item color="green">
                      添加Shipment（运输单）实体 - 跟踪运输状态
                    </Timeline.Item>
                    <Timeline.Item color="orange">
                      建立Order与新实体的关系
                    </Timeline.Item>
                  </Timeline>
                  <Alert
                    message="完整模型"
                    description="最终将形成包含5个实体、4个关系的完整订单流程模型"
                    type="success"
                  />
                </div>
              )}
            </Card>
          </Card>
        </Col>
      </Row>

      {/* 实体编辑抽屉 */}
      <Drawer
        title={`编辑实体: ${selectedEntity?.name}`}
        placement="right"
        width={400}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedEntity && (
          <Form
            layout="vertical"
            initialValues={selectedEntity}
            onFinish={(values) => {
              updateEntity({ ...selectedEntity, ...values });
              setDrawerVisible(false);
              message.success('更新成功');
            }}
          >
            <Form.Item label="实体名称" name="name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            
            <Form.Item label="实体类型" name="type" rules={[{ required: true }]}>
              <Radio.Group>
                <Radio value="aggregate">聚合根 (Aggregate)</Radio>
                <Radio value="entity">实体 (Entity)</Radio>
                <Radio value="valueObject">值对象 (Value Object)</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item label="描述" name="description">
              <TextArea rows={3} />
            </Form.Item>

            <Divider />

            <Title level={5}>属性列表</Title>
            {selectedEntity.attributes.map((attr, index) => (
              <Card key={attr.id} size="small" style={{ marginBottom: 8 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input
                    value={attr.name}
                    placeholder="属性名（如：customerId）"
                    onChange={(e) => {
                      const newAttrs = [...selectedEntity.attributes];
                      newAttrs[index].name = e.target.value;
                      setSelectedEntity({ ...selectedEntity, attributes: newAttrs });
                    }}
                  />
                  <Input
                    value={attr.description}
                    placeholder="中文描述（如：客户编号）"
                    style={{ marginTop: 8 }}
                    onChange={(e) => {
                      const newAttrs = [...selectedEntity.attributes];
                      newAttrs[index].description = e.target.value;
                      setSelectedEntity({ ...selectedEntity, attributes: newAttrs });
                    }}
                  />
                  <Select
                    value={attr.type}
                    style={{ width: '100%' }}
                    onChange={(value) => {
                      const newAttrs = [...selectedEntity.attributes];
                      newAttrs[index].type = value;
                      setSelectedEntity({ ...selectedEntity, attributes: newAttrs });
                    }}
                  >
                    <Option value="string">字符串 (String)</Option>
                    <Option value="number">数字 (Number)</Option>
                    <Option value="date">日期 (Date)</Option>
                    <Option value="boolean">布尔 (Boolean)</Option>
                    <Option value="enum">枚举 (Enum)</Option>
                  </Select>
                  <Checkbox
                    checked={attr.required}
                    onChange={(e) => {
                      const newAttrs = [...selectedEntity.attributes];
                      newAttrs[index].required = e.target.checked;
                      setSelectedEntity({ ...selectedEntity, attributes: newAttrs });
                    }}
                  >
                    必填
                  </Checkbox>
                  <Button
                    danger
                    size="small"
                    onClick={() => {
                      const newAttrs = selectedEntity.attributes.filter((_, i) => i !== index);
                      setSelectedEntity({ ...selectedEntity, attributes: newAttrs });
                    }}
                  >
                    删除属性
                  </Button>
                </Space>
              </Card>
            ))}
            
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              block
              onClick={() => {
                const newAttr: Attribute = {
                  id: `attr_${Date.now()}`,
                  name: '',
                  type: 'string',
                  required: false
                };
                setSelectedEntity({
                  ...selectedEntity,
                  attributes: [...selectedEntity.attributes, newAttr]
                });
              }}
            >
              添加属性
            </Button>

            <Divider />

            <Space>
              <Button type="primary" htmlType="submit">
                保存
              </Button>
              <Button onClick={() => setDrawerVisible(false)}>
                取消
              </Button>
            </Space>
          </Form>
        )}
      </Drawer>

      {/* 添加关系对话框 */}
      <Modal
        title="添加关系"
        open={false}
        footer={null}
      >
        <Form layout="vertical">
          <Form.Item label="源实体" rules={[{ required: true }]}>
            <Select placeholder="选择源实体">
              {entities.map(e => (
                <Option key={e.id} value={e.id}>{e.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="目标实体" rules={[{ required: true }]}>
            <Select placeholder="选择目标实体">
              {entities.map(e => (
                <Option key={e.id} value={e.id}>{e.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="关系类型" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value="1:1">1:1</Radio>
              <Radio value="1:N">1:N</Radio>
              <Radio value="N:N">N:N</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item label="关系名称" rules={[{ required: true }]}>
            <Input placeholder="如：has, belongs to, contains等" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 添加 LoadingOutlined 组件
const LoadingOutlined: React.FC = () => (
  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>
    <PlayCircleOutlined />
  </span>
);

export default InteractiveDesignTutorial;